/**
 * Drizzle query builders + query keys de FOROS, ANUNCIOS, NOTIFICACIONES y
 * BADGES (F3-C). Consumo local-first: los datos llegan por los streams de curso
 * (forum), por cohorte (forum_thread/forum_post/announcement) y privados
 * (my_notifications/my_badges). La escritura pasa por el write-path (authz de
 * moderación / read_at-only para notificaciones; badge_award es server-only).
 */

import {
  and,
  announcement,
  asc,
  badge_award,
  cohort,
  cohort_enrollment,
  cohort_teacher,
  course,
  desc,
  eq,
  forum,
  forum_post,
  forum_thread,
  isNull,
  notification,
  or,
  type PowerSyncDrizzleDB,
} from "@space/powersync";

export const communityQueryKeys = {
  all: ["academy-community"] as const,
  forumByModule: (moduleId: string | undefined) =>
    ["academy-community", "forum", "module", moduleId] as const,
  threads: (forumId: string | undefined, cohortId: string | null | undefined) =>
    ["academy-community", "threads", forumId, cohortId ?? "course-wide"] as const,
  posts: (threadId: string | undefined) => ["academy-community", "posts", threadId] as const,
  notifications: (userId: string | undefined) =>
    ["academy-community", "notifications", userId] as const,
  unread: (userId: string | undefined) =>
    ["academy-community", "notifications", "unread", userId] as const,
  badges: (userId: string | undefined) => ["academy-community", "badges", userId] as const,
  announcementsByOrg: (orgId: string | undefined) =>
    ["academy-community", "announcements", "org", orgId] as const,
  announcementsByCohort: (cohortId: string | undefined) =>
    ["academy-community", "announcements", "cohort", cohortId] as const,
  studentCohort: (courseId: string | undefined, userId: string | undefined) =>
    ["academy-community", "student-cohort", courseId, userId] as const,
  teachingCohort: (courseId: string | undefined, userId: string | undefined) =>
    ["academy-community", "teaching-cohort", courseId, userId] as const,
  courseAuthor: (courseId: string | undefined, userId: string | undefined) =>
    ["academy-community", "course-author", courseId, userId] as const,
};

// ─── Contexto de cohorte del visor para un curso ─────────────────────────────

/** Fila de contexto de cohorte (id + org) para el foro. */
export interface ForumCohortContext {
  cohort_id: string;
  organization_id: string | null;
}

/** Cohorte ACTIVA del ALUMNO para un curso (si está matriculado). */
export function getMyStudentCohortForCourseQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  courseId: string,
) {
  return db
    .select({ cohort_id: cohort.id, organization_id: cohort.organization_id })
    .from(cohort_enrollment)
    .innerJoin(cohort, eq(cohort.id, cohort_enrollment.cohort_id))
    .where(
      and(
        eq(cohort_enrollment.user_id, userId),
        eq(cohort_enrollment.status, "active"),
        isNull(cohort_enrollment.deleted_at),
        eq(cohort.course_id, courseId),
        isNull(cohort.deleted_at),
      ),
    )
    .limit(1);
}

/** Cohorte que ENSEÑO para un curso (si soy profesor). */
export function getMyTeachingCohortForCourseQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  courseId: string,
) {
  return db
    .select({ cohort_id: cohort.id, organization_id: cohort.organization_id })
    .from(cohort_teacher)
    .innerJoin(cohort, eq(cohort.id, cohort_teacher.cohort_id))
    .where(
      and(
        eq(cohort_teacher.user_id, userId),
        eq(cohort.course_id, courseId),
        isNull(cohort.deleted_at),
      ),
    )
    .limit(1);
}

/** ¿Soy AUTOR del curso? (autoría → moderación del foro course-wide). */
export function getAmICourseAuthorQuery(db: PowerSyncDrizzleDB, userId: string, courseId: string) {
  return db
    .select({ id: course.id })
    .from(course)
    .where(and(eq(course.id, courseId), eq(course.author_id, userId), isNull(course.deleted_at)))
    .limit(1);
}

// ─── Foro ─────────────────────────────────────────────────────────────────────

/** Fila `forum` de un módulo (1:1). id = `forum-<moduleId>`. */
export function getForumByModuleQuery(db: PowerSyncDrizzleDB, moduleId: string) {
  return db
    .select()
    .from(forum)
    .where(and(eq(forum.module_id, moduleId), isNull(forum.deleted_at)))
    .limit(1);
}

/**
 * Hilos visibles en el CONTEXTO de una cohorte: los de esa cohorte MÁS los
 * course-wide (cohort_id NULL). Fijados primero, luego por recencia.
 */
export function getForumThreadsQuery(
  db: PowerSyncDrizzleDB,
  forumId: string,
  cohortId: string | null,
) {
  const scope = cohortId
    ? or(eq(forum_thread.cohort_id, cohortId), isNull(forum_thread.cohort_id))
    : isNull(forum_thread.cohort_id);
  return db
    .select()
    .from(forum_thread)
    .where(and(eq(forum_thread.forum_id, forumId), isNull(forum_thread.deleted_at), scope))
    .orderBy(desc(forum_thread.pinned), desc(forum_thread.created_at));
}

/** Posts de un hilo (no borrados), en orden cronológico. Los ocultos llegan al
 * profesor (moderación); el alumno no los recibe (filtrados en el stream). */
export function getThreadPostsQuery(db: PowerSyncDrizzleDB, threadId: string) {
  return db
    .select()
    .from(forum_post)
    .where(and(eq(forum_post.thread_id, threadId), isNull(forum_post.deleted_at)))
    .orderBy(asc(forum_post.created_at));
}

// ─── Notificaciones ───────────────────────────────────────────────────────────

/** MIS notificaciones (más recientes primero). */
export function getMyNotificationsQuery(db: PowerSyncDrizzleDB, userId: string, limit = 100) {
  return db
    .select()
    .from(notification)
    .where(eq(notification.user_id, userId))
    .orderBy(desc(notification.created_at))
    .limit(limit);
}

/** MIS notificaciones NO LEÍDAS (para el contador del bell). */
export function getUnreadNotificationsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(notification)
    .where(and(eq(notification.user_id, userId), isNull(notification.read_at)));
}

// ─── Badges ─────────────────────────────────────────────────────────────────

/** MIS badges concedidos (más recientes primero). */
export function getMyBadgesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(badge_award)
    .where(eq(badge_award.user_id, userId))
    .orderBy(desc(badge_award.awarded_at));
}

// ─── Anuncios ─────────────────────────────────────────────────────────────────

/**
 * Anuncios de alcance ORG/SEDE (cohort_id NULL) de una organización.
 *
 * D9: el embargo de `publish_at` NO se filtra aquí — ya no llega lo embargado.
 * La publicación la materializa el servidor en `announcement.is_published` y el
 * stream filtra por esa columna, así que un anuncio programado a futuro solo
 * está en el dispositivo de quien lo redactó o gestiona la academia (que es
 * justamente quien tiene que verlo, marcado como programado en la vista).
 */
export function getAnnouncementsByOrgQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(announcement)
    .where(
      and(
        eq(announcement.organization_id, orgId),
        isNull(announcement.cohort_id),
        isNull(announcement.deleted_at),
      ),
    )
    .orderBy(desc(announcement.pinned), desc(announcement.publish_at));
}

/** Anuncios dirigidos a una cohorte. */
export function getAnnouncementsByCohortQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select()
    .from(announcement)
    .where(and(eq(announcement.cohort_id, cohortId), isNull(announcement.deleted_at)))
    .orderBy(desc(announcement.pinned), desc(announcement.publish_at));
}
